<template>
  <div class="bg-tool">
    <!-- Format selector on top -->
    <div class="bg-format-bar">
      <span class="bg-format-label">{{ $t('tool.BarcodeGenerator.format') }}</span>
      <el-radio-group v-model="format" size="small" @change="generate">
        <el-radio-button label="CODE128">{{ $t('tool.BarcodeGenerator.formatCode128') }}</el-radio-button>
        <el-radio-button label="EAN13">{{ $t('tool.BarcodeGenerator.formatEAN') }}</el-radio-button>
        <el-radio-button label="EAN8">EAN-8</el-radio-button>
        <el-radio-button label="UPC">{{ $t('tool.BarcodeGenerator.formatUPC') }}</el-radio-button>
        <el-radio-button label="UPCE">UPC-E</el-radio-button>
        <el-radio-button label="CODE39">{{ $t('tool.BarcodeGenerator.formatCode39') }}</el-radio-button>
        <el-radio-button label="ITF14">{{ $t('tool.BarcodeGenerator.formatITF') }}</el-radio-button>
        <el-radio-button label="Codabar">Codabar</el-radio-button>
      </el-radio-group>
      <span class="bg-format-hint">{{ formatHints[format] }}</span>
    </div>

    <!-- Input -->
    <div class="bg-input-row">
      <el-input v-model="code" :placeholder="$t('tool.BarcodeGenerator.input')" maxlength="64"
        @input="code=code.replace(/\n/g,'');generate()" size="medium" class="bg-inp"
        :class="{ 'is-error': code && validateErr }">
        <el-button slot="append" icon="el-icon-refresh" @click="randomCode">{{ $t('tool.BarcodeGenerator.random') }}</el-button>
      </el-input>
      <div class="bg-validate-err" v-if="code && validateErr">{{ validateErr }}</div>
      <div class="bg-validate-ok" v-else-if="code && !validateErr"><i class="el-icon-circle-check" /> {{ $t('tool.BarcodeGenerator.valid') }}</div>
    </div>

    <!-- Preview -->
    <div class="bg-preview" v-if="code">
      <svg ref="barcode" :viewBox="'0 0 '+svgW+' 80'" style="display:block;width:100%;height:auto;max-height:100px;"></svg>
      <div style="margin:12px 0 0;font-size:14px;font-family:Consolas,Menlo,monospace;color:#303133;letter-spacing:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;word-break:keep-all">{{ code }}</div>
      <el-button size="small" type="primary" icon="el-icon-download" @click="download" style="margin-top:14px">{{ $t('tool.BarcodeGenerator.download') }}</el-button>
    </div>

    <!-- Empty -->
    <div class="bg-empty" v-else>
      <i class="el-icon-s-grid" />
      <span>{{ $t('tool.BarcodeGenerator.hint') }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BarcodeGeneratorTool',
  props: { config: { type: Object, default: function() { return {} } } },
  data() { return { code: '', format: 'CODE128', svgW: 300 } },
  computed: {
    formatHints() {
      var h = {}
      h.CODE128 = this.$t('tool.BarcodeGenerator.hintCode128')
      h.EAN13 = this.$t('tool.BarcodeGenerator.hintEAN')
      h.EAN8 = 'EAN-8 | 7-8位纯数字，短码商品条码'
      h.UPC = this.$t('tool.BarcodeGenerator.hintUPC')
      h.UPCE = 'UPC-E | 6-8位纯数字，UPC-A零压缩短码'
      h.CODE39 = this.$t('tool.BarcodeGenerator.hintCode39')
      h.ITF14 = this.$t('tool.BarcodeGenerator.hintITF')
      h.Codabar = 'Codabar | 数字+ -$:/.+ ，图书馆/血库常用'
      return h
    },
    validateErr() {
      if (!this.code) return ''
      var v = this.code
      if (this.format === 'EAN13') {
        v = v.replace(/\D/g, '')
        if (v.length < 7) return '至少 7 位数字'
        if (v.length > 13) return '输入过长'
        return ''
      }
      if (this.format === 'EAN8') {
        v = v.replace(/\D/g, '')
        if (v.length < 7) return '至少 7 位数字'
        if (v.length > 8) return '输入过长'
        return ''
      }
      if (this.format === 'UPC') {
        v = v.replace(/\D/g, '')
        if (v.length < 6) return '至少 6 位数字'
        if (v.length > 12) return '输入过长'
        return ''
      }
      if (this.format === 'UPCE') {
        v = v.replace(/\D/g, '')
        if (v.length < 6) return '至少 6 位数字'
        if (v.length > 8) return '输入过长'
        return ''
      }
      if (this.format === 'ITF14') {
        if (/[^0-9]/.test(v)) return '仅支持纯数字'
        if (v.length < 13) return '至少 13 位数字'
        if (v.length > 14) return '输入过长'
        return ''
      }
      if (this.format === 'CODE39') {
        if (/[^0-9A-Z\-\. \$\/\+%]/.test(v.toUpperCase())) return '仅支持数字、大写字母、- . $ / + % 空格'
        return ''
      }
      if (this.format === 'Codabar') {
        if (/[^0-9A-Da-d\-\$:\/\.\+]/.test(v)) return '仅支持数字、A-D、- $ : / . +'
        return ''
      }
      // CODE128: any printable ASCII
      if (/[^\x20-\x7e]/.test(v)) return this.$t('tool.BarcodeGenerator.errCode128')
      return ''
    }
  },
  methods: {
    async generate() {
      if (!this.code) { this.$refs.barcode.innerHTML = ''; return }
      var codeToEncode = this.code
      var jsbFormat = this.format

      if (this.format === 'EAN13') {
        var raw = codeToEncode.replace(/\D/g, '')
        codeToEncode = raw.length === 13 ? raw.substring(0, 12) : raw
        jsbFormat = 'EAN13'
      } else if (this.format === 'EAN8') {
        var raw = codeToEncode.replace(/\D/g, '')
        codeToEncode = raw.length === 8 ? raw.substring(0, 7) : raw
        jsbFormat = 'EAN8'
      } else if (this.format === 'UPC') {
        var raw = codeToEncode.replace(/\D/g, '')
        codeToEncode = raw.length === 12 ? raw.substring(0, 11) : raw
        jsbFormat = 'UPC'
      } else if (this.format === 'UPCE') {
        var raw = codeToEncode.replace(/\D/g, '')
        codeToEncode = raw.substring(0, 6)
        jsbFormat = 'UPCE'
      } else if (this.format === 'ITF14') {
        codeToEncode = codeToEncode.replace(/\D/g, '')
        if (codeToEncode.length >= 13) {
          codeToEncode = codeToEncode.substring(0, 13) + this.gtinCheckDigit(codeToEncode.substring(0, 13))
        }
        jsbFormat = 'ITF14'
      } else if (this.format === 'Codabar') {
        jsbFormat = 'codabar'
      }

      try {
        const JsBarcode = (await import('jsbarcode')).default
        const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        JsBarcode(tempSvg, codeToEncode, { format: jsbFormat, width: 2, height: 68, margin: 10, displayValue: false, flat: true })
        this.$refs.barcode.innerHTML = tempSvg.innerHTML
        const vb = tempSvg.getAttribute('viewBox')
        this.svgW = vb ? parseInt(vb.split(' ')[2]) : 300
        this.$refs.barcode.setAttribute('viewBox', vb || ('0 0 ' + this.svgW + ' 80'))
      } catch(e) {
        console.error('jsbarcode error:', e.message || e); this.$refs.barcode.innerHTML = '';
      }
    },
    randomCode() {
      if (this.format === 'EAN13') {
        var d = ''; for (var i = 0; i < 12; i++) d += Math.floor(Math.random() * 10);
        this.code = d + this.checksum(d);
      } else if (this.format === 'EAN8') {
        var e8 = ''; for (var i = 0; i < 7; i++) e8 += Math.floor(Math.random() * 10);
        this.code = e8 + this.checksum(e8);
      } else if (this.format === 'UPC') {
        var u = ''; for (var i = 0; i < 11; i++) u += Math.floor(Math.random() * 10);
        this.code = u + this.checksum(u);
      } else if (this.format === 'UPCE') {
        var ue = ''; for (var i = 0; i < 6; i++) ue += Math.floor(Math.random() * 10);
        this.code = ue;
      } else if (this.format === 'ITF14') {
        var t = ''; for (var i = 0; i < 13; i++) t += Math.floor(Math.random() * 10);
        this.code = t + this.gtinCheckDigit(t);
      } else if (this.format === 'Codabar') {
        this.code = 'A' + String(Math.floor(Math.random() * 900000) + 100000) + 'B';
      } else {
        this.code = 'BC' + String(Math.floor(Math.random() * 9000000000) + 1000000000);
      }
      this.generate();
    },
    gtinCheckDigit(digits) {
      var sum = 0;
      for (var i = 0; i < digits.length; i++) {
        sum += parseInt(digits[i]) * ((digits.length - i) % 2 === 1 ? 3 : 1);
      }
      return String((10 - (sum % 10)) % 10);
    },
    checksum(digits) {
      var sum = 0;
      for (var i = 0; i < digits.length; i++) {
        sum += parseInt(digits[i]) * (i % 2 === digits.length % 2 ? 3 : 1);
      }
      return String((10 - (sum % 10)) % 10);
    },
    download() {
      var svg = this.$refs.barcode.cloneNode(true);
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      svg.setAttribute('width', this.svgW);
      svg.setAttribute('height', 80);
      var canvas = document.createElement('canvas');
      canvas.width = this.svgW * 2; canvas.height = 200;
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      var img = new Image();
      var blob = new Blob([svg.outerHTML], { type: 'image/svg+xml;charset=utf-8' });
      var url = URL.createObjectURL(blob);
      img.onload = function() {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        var a = document.createElement('a'); document.body.appendChild(a);
        a.href = canvas.toDataURL('image/png'); a.download = 'barcode.png'; a.click();
        document.body.removeChild(a);
      };
      img.src = url;
    },
    doAction(a) { if (a === 'clear') { this.code = ''; this.$refs.barcode.innerHTML = ''; } }
  }
}
</script>

<style scoped>
.bg-tool { display: flex; flex-direction: column; gap: 16px; }

/* Format selector */
.bg-format-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.bg-format-label { font-size: 13px; font-weight: 600; color: #606266; white-space: nowrap; }
.bg-format-hint { font-size: 11px; color: #b0b8c4; }
.bg-format-bar >>> .el-radio-button__inner { font-size: 12px; padding: 6px 14px; }

/* Input */
.bg-input-row { }
.bg-inp >>> input { font-family: Consolas,Menlo,monospace; font-size: 15px; letter-spacing: 1px; }
.bg-inp.is-error >>> input { border-color: #f56c6c !important; }
.bg-validate-err { font-size: 12px; color: #f56c6c; margin-top: 6px; padding-left: 4px; }
.bg-validate-ok { font-size: 12px; color: #67c23a; margin-top: 6px; padding-left: 4px; display: flex; align-items: center; gap: 4px; }

/* Preview */
.bg-preview { text-align: center; padding: 24px 20px; background: #fff; border-radius: 12px; border: 1px solid #ebeef5; }

/* Empty */
.bg-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 40px 20px; color: #c0c4cc; }
.bg-empty i { font-size: 42px; }

@media (max-width: 640px) {
  .bg-format-bar >>> .el-radio-button__inner { padding: 5px 10px; font-size: 11px; }
}
</style>
